import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
} from 'react-router-dom'
import { ToastContainer } from 'react-toastify'
import { ClerkProvider } from '@clerk/react'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'

import ErrorBoundary from '@/components/ErrorBoundary'
import { MainLayout } from '@/components/MainLayout'
import { TFLViewerLayout } from '@/components/TFLViewerLayout'
import AuthProvider from '@/features/auth/AuthProvider'
import { ProtectedRoute } from '@/features/auth/ProtectedRoute'
import { RoleBasedRedirect } from '@/features/auth/RoleBasedRedirect'
import { NavigationBlockProvider, UploadProvider } from '@/features/file-upload'
import { WebPubSubProvider } from '@/features/notification'
import { ERoleType } from '@/features/user-management/constants'
import { queryClient } from '@/lib/queryClient'
import LoginPage from '@/pages/Authentication/LoginPage'
import SignUpPage from '@/pages/Authentication/SignUpPage'
import CommentListPage from '@/pages/Comment/ListPage'
import CommentTemplateListPage from '@/pages/CommentTemplate/ListPage'
import CompanyDetailPage from '@/pages/Company/CompanyDetailPage'
import CompanyPage from '@/pages/Company/CompanyPage'
import DashboardPage from '@/pages/DashboardPage'
import DeliverableSetDetailPage from '@/pages/DeliverableSet/DetailPage'
import DeliverableSetsPage from '@/pages/DeliverableSet/ListPage'
import DocumentPage from '@/pages/DocumentPage'
import UploadPage from '@/pages/FileUpload/UploadPage'
import HistoryListPage from '@/pages/History/ListPage'
import InternalServerError from '@/pages/InternalServerError'
import InvitePage from '@/pages/InvitePage'
import MyWorkPage from '@/pages/MyWork/MyWorkPage'
import PageNotFound from '@/pages/PageNotFound'
import ProjectDetailPage from '@/pages/Project/DetailPage'
import ProjectListPage from '@/pages/Project/ListPage'
import NotificationSettingsPage from '@/pages/Settings/NotificationSettingsPage'
import TeamWorkIntegration from '@/pages/TeamworkIntegration'
import UnauthorizedPage from '@/pages/UnauthorizedPage'
import UserManagementPage from '@/pages/UserManagement/UserManagementPage'
import UserMappingPage from '@/pages/UserMappingPage'
import WorkflowConfigPage from '@/pages/WorkflowConfig/WorkflowConfigPage'
import { getNestedRoutePath, ROUTES } from '@/utils/routes'

import './i18n'

import { useClerkConfig } from './client/clerkConfig'

import 'react-toastify/dist/ReactToastify.css'
import './styles/globals.scss'
import './styles/variables.scss'
import './pages/Authentication/styles.scss'

function App() {
  const clerkConfig = useClerkConfig()

  return (
    <QueryClientProvider client={queryClient}>
      <ToastContainer />
      <UploadProvider>
        <NavigationBlockProvider>
          <ErrorBoundary>
            <ClerkProvider {...clerkConfig}>
              <AuthProvider autoConnect>
                <Router>
                  <WebPubSubProvider autoConnect>
                    <Routes>
                      <Route path={ROUTES.LOGIN} element={<LoginPage />} />
                      <Route path={ROUTES.SIGN_UP} element={<SignUpPage />} />
                      <Route path={ROUTES.INVITE} element={<InvitePage />} />
                      <Route index element={<RoleBasedRedirect />} />
                      <Route
                        path={ROUTES.UNAUTHORIZED}
                        element={<UnauthorizedPage />}
                      />
                      <Route
                        path={ROUTES.NOT_FOUND}
                        element={<PageNotFound />}
                      />
                      <Route
                        path={ROUTES.INTERNAL_SERVER_ERROR}
                        element={<InternalServerError />}
                      />

                      {/* Protected routes - Main Layout */}
                      <Route
                        path={ROUTES.ROOT}
                        element={
                          <ProtectedRoute
                            requiredRoles={[
                              ERoleType.ORGANIZATION_ADMIN,
                              ERoleType.PROJECT_LEAD,
                              ERoleType.TEAM_MEMBER,
                              ERoleType.REVIEWER,
                              ERoleType.VIEWER,
                            ]}
                          >
                            <MainLayout />
                          </ProtectedRoute>
                        }
                      >
                        <Route
                          path={getNestedRoutePath(ROUTES.MY_WORK)}
                          element={
                            <ProtectedRoute
                              requiredRoles={[
                                ERoleType.ORGANIZATION_ADMIN,
                                ERoleType.PROJECT_LEAD,
                                ERoleType.TEAM_MEMBER,
                                ERoleType.REVIEWER,
                                ERoleType.VIEWER,
                              ]}
                            >
                              <MyWorkPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path={getNestedRoutePath(ROUTES.COMPANY)}
                          element={
                            <ProtectedRoute
                              requiredRoles={[ERoleType.ORGANIZATION_ADMIN]}
                            >
                              <CompanyPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path={`${getNestedRoutePath(ROUTES.COMPANY)}/:id`}
                          element={
                            <ProtectedRoute
                              requiredRoles={[ERoleType.ORGANIZATION_ADMIN]}
                            >
                              <CompanyDetailPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path={`${getNestedRoutePath(ROUTES.PROJECT)}/:id`}
                          element={
                            <ProtectedRoute
                              requiredRoles={[
                                ERoleType.ORGANIZATION_ADMIN,
                                ERoleType.PROJECT_LEAD,
                                ERoleType.TEAM_MEMBER,
                              ]}
                            >
                              <ProjectDetailPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path={getNestedRoutePath(ROUTES.PROJECT)}
                          element={
                            <ProtectedRoute
                              requiredRoles={[
                                ERoleType.ORGANIZATION_ADMIN,
                                ERoleType.PROJECT_LEAD,
                                ERoleType.TEAM_MEMBER,
                              ]}
                            >
                              <ProjectListPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path={getNestedRoutePath(ROUTES.DELIVERABLE_SET)}
                          element={
                            <ProtectedRoute
                              requiredRoles={[
                                ERoleType.ORGANIZATION_ADMIN,
                                ERoleType.PROJECT_LEAD,
                                ERoleType.TEAM_MEMBER,
                              ]}
                            >
                              <DeliverableSetsPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path={`${getNestedRoutePath(ROUTES.DELIVERABLE_SET)}/:id`}
                          element={
                            <ProtectedRoute
                              requiredRoles={[
                                ERoleType.ORGANIZATION_ADMIN,
                                ERoleType.PROJECT_LEAD,
                                ERoleType.TEAM_MEMBER,
                                ERoleType.REVIEWER,
                                ERoleType.VIEWER,
                              ]}
                            >
                              <DeliverableSetDetailPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path={getNestedRoutePath(ROUTES.COMMENT)}
                          element={
                            <ProtectedRoute
                              requiredRoles={[
                                ERoleType.ORGANIZATION_ADMIN,
                                ERoleType.PROJECT_LEAD,
                                ERoleType.TEAM_MEMBER,
                              ]}
                            >
                              <CommentListPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path={getNestedRoutePath(ROUTES.COMMENT_TEMPLATES)}
                          element={
                            <ProtectedRoute
                              requiredRoles={[
                                ERoleType.ORGANIZATION_ADMIN,
                                ERoleType.PROJECT_LEAD,
                              ]}
                            >
                              <CommentTemplateListPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path={getNestedRoutePath(
                            ROUTES.TEAMWORK_INTEGRATIONS
                          )}
                          element={
                            <ProtectedRoute
                              requiredRoles={[ERoleType.ORGANIZATION_ADMIN]}
                            >
                              <TeamWorkIntegration />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path={getNestedRoutePath(ROUTES.USER_MANAGEMENT)}
                          element={
                            <ProtectedRoute
                              requiredRoles={[
                                ERoleType.ORGANIZATION_ADMIN,
                                ERoleType.PROJECT_LEAD,
                              ]}
                            >
                              <UserManagementPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path={getNestedRoutePath(ROUTES.USER_MAPPING)}
                          element={
                            <ProtectedRoute
                              requiredRoles={[ERoleType.ORGANIZATION_ADMIN]}
                            >
                              <UserMappingPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path={getNestedRoutePath(ROUTES.FILE_UPLOAD_BASE)}
                          element={
                            <ProtectedRoute
                              requiredRoles={[
                                ERoleType.ORGANIZATION_ADMIN,
                                ERoleType.PROJECT_LEAD,
                                ERoleType.TEAM_MEMBER,
                              ]}
                            >
                              <UploadPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path={getNestedRoutePath(
                            ROUTES.FILE_UPLOAD_FROM_DETAIL
                          )}
                          element={
                            <ProtectedRoute
                              requiredRoles={[
                                ERoleType.ORGANIZATION_ADMIN,
                                ERoleType.PROJECT_LEAD,
                                ERoleType.TEAM_MEMBER,
                              ]}
                            >
                              <UploadPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path={getNestedRoutePath(
                            ROUTES.FILE_UPLOAD_FROM_DOCUMENT
                          )}
                          element={
                            <ProtectedRoute
                              requiredRoles={[
                                ERoleType.ORGANIZATION_ADMIN,
                                ERoleType.PROJECT_LEAD,
                                ERoleType.TEAM_MEMBER,
                              ]}
                            >
                              <UploadPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path={getNestedRoutePath(
                            ROUTES.NOTIFICATION_SETTINGS
                          )}
                          element={<NotificationSettingsPage />}
                        />
                        <Route
                          path={getNestedRoutePath(ROUTES.WORKFLOW_CONFIG)}
                          element={
                            <ProtectedRoute
                              requiredRoles={[ERoleType.ORGANIZATION_ADMIN]}
                            >
                              <WorkflowConfigPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path={getNestedRoutePath(ROUTES.DASHBOARD)}
                          element={
                            <ProtectedRoute
                              requiredRoles={[
                                ERoleType.ORGANIZATION_ADMIN,
                                ERoleType.PROJECT_LEAD,
                              ]}
                            >
                              <DashboardPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path={getNestedRoutePath(ROUTES.HISTORY)}
                          element={
                            <ProtectedRoute
                              requiredRoles={[ERoleType.ORGANIZATION_ADMIN]}
                            >
                              <HistoryListPage />
                            </ProtectedRoute>
                          }
                        />
                      </Route>

                      <Route
                        path={`${getNestedRoutePath(ROUTES.DELIVERABLE_SET)}/:id/document`}
                        element={
                          <ProtectedRoute
                            requiredRoles={[
                              ERoleType.ORGANIZATION_ADMIN,
                              ERoleType.PROJECT_LEAD,
                              ERoleType.TEAM_MEMBER,
                              ERoleType.REVIEWER,
                              ERoleType.VIEWER,
                            ]}
                          >
                            <TFLViewerLayout />
                          </ProtectedRoute>
                        }
                      >
                        <Route index element={<DocumentPage />} />
                      </Route>

                      <Route
                        path="*"
                        element={<Navigate to={ROUTES.ROOT} replace />}
                      />
                    </Routes>
                  </WebPubSubProvider>
                </Router>
              </AuthProvider>
            </ClerkProvider>
          </ErrorBoundary>
        </NavigationBlockProvider>
      </UploadProvider>

      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  )
}

export default App
